import { api } from '@/helpers/apiClient'
import { logRequestFailure } from '@/helpers/request-outcome'
import { pageQuery } from '@/lib/pagination'

import {
  venueDtoSchema,
  venueListDtoSchema,
  venueManagerListDtoSchema,
  venueSpaceDtoSchema,
  type VenueDto,
  type VenueListDto,
  type VenueManagerListDto,
  type VenueSpaceDto,
  timezoneListDtoSchema,
  type TimezoneListDto,
} from '@lib/dto/venue.dto'
import {
  spaceImportApplyResultDtoSchema,
  spaceImportPreviewDtoSchema,
  type SpaceImportApplyResultDto,
  type SpaceImportPreviewDto,
} from '@lib/dto/space-import.dto'
import type {
  CreateEventTrackInput,
  CreateSpaceVersionInput,
  CreateVenueInput,
  CreateVenueSpaceInput,
  UpdateEventTrackInput,
  UpdateSpaceVersionInput,
  UpdateVenueInput,
  UpdateVenueScheduleInput,
  UpdateVenueSpaceInput,
} from '@lib/input/venue.input'
import type { ApplySpaceImportInput, PreviewSpaceImportInput } from '@lib/input/space-import.input'
import type { IVenueService } from '@/types/VenueService.types'
import type {
  AddSpacePhotoInput,
  ReorderSpacePhotosInput,
  UpdateSpacePhotoInput,
} from '@lib/input/space-photo.input'
import type { SetCoverImageInput } from '@lib/input/cover-image.input'

/**
 * Service for the Venue REST endpoints. Responses are validated against the
 * shared `@lib` schemas at the boundary. Results are tenant-scoped by the API.
 */
class VenueService implements IVenueService {
  async list(page?: number): Promise<VenueListDto> {
    try {
      return await api.get(`/venues${pageQuery(page)}`, venueListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to list venues', {}, error)
      throw error
    }
  }

  /**
   * The time zones a venue's clock may be set to.
   *
   * From the API rather than `Intl.supportedValuesOf` in the browser: the two
   * runtimes are upgraded at different times, so a locally-built list could
   * offer a name the server refuses.
   */
  async timezones(): Promise<TimezoneListDto> {
    try {
      return await api.get('/venues/timezones', timezoneListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load time zones', {}, error)
      throw error
    }
  }

  async get(id: string): Promise<VenueDto> {
    try {
      return await api.get(`/venues/${id}`, venueDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load venue', { id }, error)
      throw error
    }
  }

  async create(input: CreateVenueInput): Promise<VenueDto> {
    try {
      return await api.post('/venues', input, venueDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to create venue', {}, error)
      throw error
    }
  }

  async update(id: string, input: UpdateVenueInput): Promise<VenueDto> {
    try {
      return await api.put(`/venues/${id}`, input, venueDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to update venue', { id }, error)
      throw error
    }
  }

  /**
   * Set the venue's clock and usual evening. Its own route because it is a venue
   * admin's business — see `updateVenueScheduleInputSchema`.
   */
  async updateSchedule(id: string, input: UpdateVenueScheduleInput): Promise<VenueDto> {
    try {
      return await api.put(`/venues/${id}/schedule`, input, venueDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to update venue schedule', { id }, error)
      throw error
    }
  }

  /**
   * Set or clear a venue's cover photograph. Its own route because it has its
   * own permission: a venue admin may change the photograph of a venue they
   * manage without being able to rename it.
   */
  async setCover(id: string, input: SetCoverImageInput): Promise<VenueDto> {
    try {
      // Each field omitted unless given: the picker sends a point without an
      // image, and an upload sends an image without a point. Sending the
      // displayed `coverImageUrl` back to say where to crop is what stored a
      // signed URL over the venue's object key.
      return await api.put(`/venues/${id}/cover`, input, venueDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to set venue cover', { id }, error)
      throw error
    }
  }

  /** Soft-delete a venue (204 on success). Archived venues leave lists/pickers. */
  async delete(id: string): Promise<void> {
    try {
      await api.del(`/venues/${id}`)
    } catch (error) {
      logRequestFailure('Failed to delete venue', { id }, error)
      throw error
    }
  }

  async createSpace(venueId: string, input: CreateVenueSpaceInput): Promise<VenueDto> {
    try {
      return await api.post(`/venues/${venueId}/spaces`, input, venueDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to create venue space', { venueId }, error)
      throw error
    }
  }

  async updateSpace(venueId: string, spaceId: string, input: UpdateVenueSpaceInput): Promise<VenueSpaceDto> {
    try {
      return await api.put(`/venues/${venueId}/spaces/${spaceId}`, input, venueSpaceDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to update venue space', { venueId, spaceId }, error)
      throw error
    }
  }

  // ---- space photographs ---------------------------------------------------

  /** Add a photograph. `image` is a storage key from the upload service, or a data URL. */
  async addSpacePhoto(venueId: string, spaceId: string, input: AddSpacePhotoInput): Promise<VenueSpaceDto> {
    try {
      return await api.post(`/venues/${venueId}/spaces/${spaceId}/photos`, input, venueSpaceDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to add space photo', { venueId, spaceId }, error)
      throw error
    }
  }

  async updateSpacePhoto(
    venueId: string,
    spaceId: string,
    photoId: string,
    input: UpdateSpacePhotoInput,
  ): Promise<VenueSpaceDto> {
    try {
      return await api.patch(
        `/venues/${venueId}/spaces/${spaceId}/photos/${photoId}`,
        input,
        venueSpaceDtoSchema,
      )
    } catch (error) {
      logRequestFailure('Failed to update space photo', { venueId, spaceId, photoId }, error)
      throw error
    }
  }

  async deleteSpacePhoto(venueId: string, spaceId: string, photoId: string): Promise<VenueSpaceDto> {
    try {
      return await api.delFor(`/venues/${venueId}/spaces/${spaceId}/photos/${photoId}`, venueSpaceDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to delete space photo', { venueId, spaceId, photoId }, error)
      throw error
    }
  }

  /** Send every photograph id, in the order they should appear. */
  async reorderSpacePhotos(
    venueId: string,
    spaceId: string,
    input: ReorderSpacePhotosInput,
  ): Promise<VenueSpaceDto> {
    try {
      return await api.put(`/venues/${venueId}/spaces/${spaceId}/photos/order`, input, venueSpaceDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to reorder space photos', { venueId, spaceId }, error)
      throw error
    }
  }

  /** Delete a space (blocked when any event phase uses it). */
  async deleteSpace(venueId: string, spaceId: string): Promise<void> {
    try {
      await api.del(`/venues/${venueId}/spaces/${spaceId}`)
    } catch (error) {
      logRequestFailure('Failed to delete venue space', { venueId, spaceId }, error)
      throw error
    }
  }

  async getSpaceVersion(venueId: string, spaceId: string, versionId: string): Promise<VenueSpaceDto> {
    try {
      return await api.get(`/venues/${venueId}/spaces/${spaceId}/versions/${versionId}`, venueSpaceDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load space version', { venueId, spaceId, versionId }, error)
      throw error
    }
  }

  async createSpaceVersion(venueId: string, spaceId: string, input: CreateSpaceVersionInput): Promise<VenueSpaceDto> {
    try {
      return await api.post(`/venues/${venueId}/spaces/${spaceId}/versions`, input, venueSpaceDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to create space version', { venueId, spaceId }, error)
      throw error
    }
  }

  async updateSpaceVersion(
    venueId: string,
    spaceId: string,
    versionId: string,
    input: UpdateSpaceVersionInput,
  ): Promise<VenueSpaceDto> {
    try {
      return await api.put(`/venues/${venueId}/spaces/${spaceId}/versions/${versionId}`, input, venueSpaceDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to update space version', { venueId, spaceId, versionId }, error)
      throw error
    }
  }

  /** Dry-run a legacy layout SVG as a space floorplan background. */
  async previewSpaceImport(
    venueId: string,
    spaceId: string,
    input: PreviewSpaceImportInput,
  ): Promise<SpaceImportPreviewDto> {
    try {
      return await api.post(
        `/venues/${venueId}/spaces/${spaceId}/import/preview`,
        input,
        spaceImportPreviewDtoSchema,
      )
    } catch (error) {
      logRequestFailure('Failed to preview space import', { venueId, spaceId }, error)
      throw error
    }
  }

  /** Apply a legacy layout SVG as a space floorplan (dimensions + zones). */
  async applySpaceImport(
    venueId: string,
    spaceId: string,
    input: ApplySpaceImportInput,
  ): Promise<SpaceImportApplyResultDto> {
    try {
      return await api.post(
        `/venues/${venueId}/spaces/${spaceId}/import/apply`,
        input,
        spaceImportApplyResultDtoSchema,
      )
    } catch (error) {
      logRequestFailure('Failed to apply space import', { venueId, spaceId }, error)
      throw error
    }
  }

  async deleteSpaceVersion(venueId: string, spaceId: string, versionId: string): Promise<void> {
    try {
      await api.del(`/venues/${venueId}/spaces/${spaceId}/versions/${versionId}`)
    } catch (error) {
      logRequestFailure('Failed to delete space version', { venueId, spaceId, versionId }, error)
      throw error
    }
  }

  async createTrack(venueId: string, input: CreateEventTrackInput): Promise<VenueDto> {
    try {
      return await api.post(`/venues/${venueId}/tracks`, input, venueDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to create track', { venueId }, error)
      throw error
    }
  }

  async updateTrack(venueId: string, trackId: string, input: UpdateEventTrackInput): Promise<VenueDto> {
    try {
      return await api.put(`/venues/${venueId}/tracks/${trackId}`, input, venueDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to update track', { venueId, trackId }, error)
      throw error
    }
  }

  async deleteTrack(venueId: string, trackId: string): Promise<void> {
    try {
      await api.del(`/venues/${venueId}/tracks/${trackId}`)
    } catch (error) {
      logRequestFailure('Failed to delete track', { venueId, trackId }, error)
      throw error
    }
  }

  async listManagers(venueId: string): Promise<VenueManagerListDto> {
    try {
      return await api.get(`/venues/${venueId}/managers`, venueManagerListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to list venue managers', { venueId }, error)
      throw error
    }
  }

  async assignManager(venueId: string, userId: string): Promise<VenueManagerListDto> {
    try {
      return await api.post(`/venues/${venueId}/managers`, { userId }, venueManagerListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to assign venue manager', { venueId, userId }, error)
      throw error
    }
  }

  async unassignManager(venueId: string, userId: string): Promise<void> {
    try {
      await api.del(`/venues/${venueId}/managers/${userId}`)
    } catch (error) {
      logRequestFailure('Failed to unassign venue manager', { venueId, userId }, error)
      throw error
    }
  }
}

export const venueService = new VenueService()
